import React from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';

// Text pairs are [english, tamil].
const keyInstructions = [
  {
    title: ['Enter Valid Email Address', 'சரியான மின்னஞ்சல் முகவரியை உள்ளிடவும்'],
    description: [
      'Use a valid email address as this will be your username for logging into the portal.',
      'இணையதளத்தில் உள்நுழைவதற்கான பயனர் பெயராக இது பயன்படுத்தப்படுவதால், சரியான மின்னஞ்சல் முகவரியைப் பயன்படுத்தவும்.',
    ],
  },
  {
    title: ['Upload Required Files', 'தேவையான ஆவணங்களைப் பதிவேற்றவும்'],
    description: [
      'Upload all mandatory files as specified in the form.',
      'படிவத்தில் குறிப்பிட்டுள்ள அனைத்துக் கட்டாய ஆவணங்களையும் பதிவேற்றவும்.',
    ],
  },
  {
    title: ['Provide Accurate Contact Information', 'சரியான தொடர்பு விவரங்களை வழங்கவும்'],
    description: [
      'Enter correct contact details for the person who will handle NOC applications and communications.',
      'தடையின்மைச் சான்று விண்ணப்பங்கள் மற்றும் தகவல் தொடர்புகளைக் கையாளும் நபரின் சரியான தொடர்பு விவரங்களை உள்ளிடவும்.',
    ],
  },
  {
    title: ['Complete All Mandatory Fields', 'அனைத்துக் கட்டாயப் புலங்களையும் நிரப்பவும்'],
    description: [
      'Fill in all fields marked with (*) before submitting the registration form.',
      'பதிவுப் படிவத்தைச் சமர்ப்பிக்கும் முன் (*) குறியிட்ட அனைத்துப் புலங்களையும் நிரப்பவும்.',
    ],
  },
];

const organizationDocs = [
  { text: ['Registration Certificate', 'பதிவுச் சான்றிதழ்'], required: true },
  { text: ['PAN Card Copy', 'பான் அட்டை நகல்'], required: true },
  { text: ['Organization Profile', 'அமைப்பின் விவரக்குறிப்பு'], required: true },
  { text: ['Similar Project Details', 'இதே போன்ற திட்ட விவரங்கள்'], required: false },
];

const individualDocs = [
  { text: ['Aadhaar Card Copy', 'ஆதார் அட்டை நகல்'], required: true },
  { text: ['PAN Card Copy', 'பான் அட்டை நகல்'], required: false },
  { text: ['Similar Project Details', 'இதே போன்ற திட்ட விவரங்கள்'], required: false },
];

const DocList = ({ docs, tr }) => (
  <ul className="divide-y divide-slate-100">
    {docs.map((doc, i) => (
      <li key={doc.text[0]} className="flex items-start gap-3 px-5 py-3 odd:bg-white even:bg-slate-50">
        <span className="text-slate-500 font-medium text-sm w-5">{i + 1}.</span>
        <span className={`text-sm ${doc.required ? 'text-slate-900 font-medium' : 'text-slate-600'}`}>
          {tr(...doc.text)}
          {doc.required ? <span className="text-rose-600"> *</span> : <span className="text-slate-400"> ({tr('Optional', 'விருப்பத்தேர்வு')})</span>}
        </span>
      </li>
    ))}
  </ul>
);

const DocumentsPage = () => {
  const { tr } = useLang();

  return (
    <div className="min-h-screen bg-slate-100 pt-20">
      {/* Page header */}
      <div className="relative bg-cover bg-center border-b-4 border-brand-accent" style={{ backgroundImage: "url('/images/water3.jpg')" }}>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-700/95 via-brand-600/85 to-teal-500/70" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 py-10">
          <h1 className="text-3xl lg:text-4xl font-semibold text-white">
            {tr('Organization Registration Requirements', 'அமைப்புப் பதிவுக்கான தேவைகள்')}
          </h1>
          <p className="mt-2 text-white/90 max-w-2xl">
            {tr(
              'Follow these key instructions for completing your Organization Registration Form.',
              'உங்கள் அமைப்புப் பதிவுப் படிவத்தை நிறைவு செய்ய இந்த முக்கிய வழிமுறைகளைப் பின்பற்றவும்.'
            )}
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10 space-y-8">
        {/* Key Instructions */}
        <section className="bg-white border border-slate-200">
          <div className="px-5 py-3 bg-brand-600">
            <h2 className="text-base font-semibold text-white">{tr('Registration Instructions', 'பதிவு வழிமுறைகள்')}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4 p-5">
            {keyInstructions.map((ins, i) => (
              <div key={ins.title[0]} className="flex items-start gap-4 p-4 border border-slate-200 bg-slate-50">
                <div className="w-8 h-8 bg-brand-700 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 mb-1">{tr(...ins.title)}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{tr(...ins.description)}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Required Documents */}
        <section className="bg-white border border-slate-200">
          <div className="px-5 py-3 bg-brand-600">
            <h2 className="text-base font-semibold text-white">{tr('Required Documents Checklist', 'தேவையான ஆவணங்களின் பட்டியல்')}</h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-5 p-5">
            <div className="border border-sky-200">
              <h3 className="px-5 py-3 bg-sky-600 text-white font-semibold">
                {tr('Organization Related Documents', 'அமைப்பு தொடர்பான ஆவணங்கள்')}
              </h3>
              <DocList docs={organizationDocs} tr={tr} />
            </div>
            <div className="border border-emerald-200">
              <h3 className="px-5 py-3 bg-emerald-600 text-white font-semibold">
                {tr('Individual Related Documents', 'தனிநபர் தொடர்பான ஆவணங்கள்')}
              </h3>
              <DocList docs={individualDocs} tr={tr} />
            </div>
          </div>
        </section>

        <div className="text-center">
          <Link to="/register" className="inline-block px-8 py-3 bg-brand-700 text-white font-semibold hover:bg-brand-800">
            {tr('Register Now for NOC', 'தடையின்மைச் சான்றுக்கு இப்போதே பதிவு செய்க')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DocumentsPage;
